import { useRef, useState, useMemo, useCallback } from 'react';
import { UploadCloud, FileCheck2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useSubmitRequest } from '@/api/requests';
import { useFiles, uploadFile } from '@/api/files';
import { useFolders } from '@/api/folders';
import { useWorkspace } from '@/api/workspaces';
import { useQueryClient } from '@tanstack/react-query';
import { useUIStore } from '@/stores/ui';
import { useAuth } from '@/hooks/useAuth';
import { apiError } from '@/lib/api';

interface SubmitRequestModalProps {
  open: boolean;
  onClose: () => void;
  requestId: string;
  targetFolderId?: string;
}

export function SubmitRequestModal({ open, onClose, requestId, targetFolderId }: SubmitRequestModalProps) {
  const [fileId, setFileId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadedName, setUploadedName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const submit = useSubmitRequest();
  const push = useUIStore((s) => s.pushToast);
  const qc = useQueryClient();
  const { user, activeOrgId } = useAuth();

  const [selectedFolderId, setSelectedFolderId] = useState<string>('');

  const orgId = activeOrgId ?? user?.organization_id ?? null;
  const workspaceQuery = useWorkspace(!targetFolderId ? orgId : null);
  const workspaceId = workspaceQuery.data?.id ?? null;

  const allFoldersQuery = useFolders(!targetFolderId ? workspaceId : null);
  const flatFolders = useMemo(
    () => (allFoldersQuery.data ?? []).filter((f) => !f.is_archived),
    [allFoldersQuery.data],
  );

  const activeFolderId = targetFolderId ?? selectedFolderId ?? null;

  const folderFiles = useFiles(activeFolderId ?? undefined);
  const availableFiles = useMemo(
    () => (folderFiles.data ?? []).filter((f) => !f.is_deleted),
    [folderFiles.data],
  );

  const canSubmit = fileId.trim().length > 0 && !uploading;

  const doUpload = useCallback(async (file: File) => {
    if (!activeFolderId) return;
    setUploading(true);
    setUploadPercent(0);
    setUploadedName(file.name);
    try {
      const created = await uploadFile(activeFolderId, file, (p) => {
        setUploadPercent(p.percent);
      });
      setFileId(created.id);
      qc.invalidateQueries({ queryKey: ['files', activeFolderId] });
      push({ title: `"${file.name}" uploaded`, variant: 'success' });
    } catch (err) {
      setUploadedName('');
      setUploadPercent(0);
      push({ title: 'Upload failed', body: apiError(err), variant: 'error' });
    } finally {
      setUploading(false);
    }
  }, [activeFolderId, qc, push]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await doUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!uploading && activeFolderId) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading || !activeFolderId) return;
    const file = e.dataTransfer.files?.[0];
    if (file) await doUpload(file);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await submit.mutateAsync({ id: requestId, file_id: fileId.trim() });
      push({ title: 'Request submitted for review', variant: 'success' });
      setFileId(''); setUploadedName(''); setSelectedFolderId('');
      onClose();
    } catch (e) {
      push({ title: 'Submit failed', body: apiError(e), variant: 'error' });
    }
  };

  const isLoadingFolders = !targetFolderId && (workspaceQuery.isLoading || allFoldersQuery.isLoading);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Submit for review"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={submit.isPending} disabled={!canSubmit} className="w-auto">
            Submit
          </Button>
        </>
      }
    >
      {/* Folder picker (when no targetFolderId provided) */}
      {!targetFolderId && (
        <div className="mb-4">
          <Label>Folder</Label>
          {isLoadingFolders ? (
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500"><Spinner size={14} /> Loading folders…</div>
          ) : flatFolders.length === 0 ? (
            <p className="text-xs text-slate-400 mt-1">No folders found in your workspace.</p>
          ) : (
            <Select
              value={selectedFolderId}
              onChange={(v) => { setSelectedFolderId(v); setFileId(''); setUploadedName(''); }}
              options={flatFolders.map((f) => ({ value: f.id, label: f.name }))}
              placeholder="Select a folder…"
            />
          )}
        </div>
      )}

      {/* Upload a new file */}
      {activeFolderId ? (
        <div className="mb-4">
          <Label>Upload a file</Label>
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`mt-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg px-4 py-6 cursor-pointer transition-colors ${
              isDragging
                ? 'border-[#0410BD] bg-[#EFF0FF]'
                : uploading
                ? 'border-[#E3E3F1] bg-slate-50 cursor-not-allowed'
                : 'border-[#E3E3F1] hover:border-[#0410BD] hover:bg-[#EFF0FF]'
            }`}
          >
            {uploading ? <Spinner size={24} /> : uploadedName ? <FileCheck2 size={24} className="text-emerald-500 shrink-0" /> : <UploadCloud size={24} className="text-[#0410BD] shrink-0" />}
            <div className="text-center w-full">
              <p className="text-sm font-medium text-[#12122C]">
                {uploading
                  ? (uploadPercent >= 100 ? `Finalizing…` : `Uploading ${uploadedName}…`)
                  : uploadedName ? uploadedName : 'Drop a file here or click to browse'}
              </p>
              {uploading && (
                <div className="mt-2 mx-auto w-48">
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#0410BD] transition-[width] duration-200"
                      style={{ width: `${uploadPercent}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {uploadPercent >= 100 ? 'Finalizing…' : `${uploadPercent}%`}
                  </p>
                </div>
              )}
              {!uploading && !uploadedName && (
                <p className="text-xs text-slate-400 mt-0.5">Supports all file types</p>
              )}
            </div>
          </div>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
        </div>
      ) : !targetFolderId && !isLoadingFolders && flatFolders.length > 0 ? (
        <div className="mb-4">
          <Label>Upload a file</Label>
          <div className="mt-1 flex items-center gap-3 border-2 border-dashed border-[#E3E3F1] rounded-lg px-4 py-4 opacity-50 select-none">
            <UploadCloud size={20} className="text-slate-400 shrink-0" />
            <span className="text-sm text-slate-400">Select a folder above to upload</span>
          </div>
        </div>
      ) : null}

      {/* Pick from existing files in folder */}
      {activeFolderId && availableFiles.length > 0 && (
        <div className="mb-3">
          <Label>Or pick an existing file</Label>
          {folderFiles.isLoading ? (
            <div className="py-2"><Spinner /></div>
          ) : (
            <div className="border border-[#E3E3F1] rounded-lg overflow-hidden max-h-40 overflow-y-auto">
              {availableFiles.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => { setFileId(f.id); setUploadedName(f.name); }}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-[#E3E3F1] last:border-0 transition-colors ${fileId === f.id ? 'bg-[#EFF0FF] text-[#0410BD] font-medium' : 'text-slate-800 hover:bg-[#F2F2F8]'}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
